import { AiCreditLedgerEntry } from 'src/types/TAiSettings';

// Display labels for the AI usage page: per-rate operations and ledger
// sources. Kept apart from AiUsage.tsx so they can be unit-tested without
// pulling in the page's form dependencies.

export const OPERATION_LABELS: Record<string, string> = {
  bgremove: 'Background Removal',
  image_analysis: 'Image Analysis (OCR / Vision)',
  // Video, per minute of uploaded video (conf/22)
  video_sd: 'Video: SD MP4 (per minute)',
  video_hd: 'Video: HD MP4 (per minute)',
  video_4k: 'Video: 4K MP4 (per minute)',
  video_hls: 'Video: Adaptive Streaming (per minute)',
  video_storyboard: 'Video: Storyboard (per minute)',
  agent_step: 'Agent Step',
  default: 'AI Chat (default token rate)',
  // AI Image Edit — P1 enhancement (hosted Fal models)
  auto_enhance: 'Auto Enhance',
  ai_restore: 'AI Restore',
  white_balance: 'White Balance',
  exposure_correction: 'Exposure Correction',
  color: 'Color Correction',
  deblur: 'Deblur',
  upscale: 'Upscale',
  face_restore: 'Face Restore',
  sequence: 'Enhance Sequence',
  // AI Image Edit — P2 edit verbs
  remove_background: 'Remove Background',
  erase: 'Erase',
  inpaint: 'Inpaint (Generative Fill)',
  replace_background: 'Replace Background',
  expand: 'Expand',
  search_replace: 'Search & Replace',
  product_crop: 'Product Crop',
  // AI Image Edit — P3 generative
  text_to_image: 'Text to Image',
  generative_fill: 'Generative Fill',
  generative_expand: 'Generative Expand',
  text_to_image_cs: 'Text to Image (commercial-safe)',
  generative_fill_cs: 'Generative Fill (commercial-safe)',
  generative_expand_cs: 'Generative Expand (commercial-safe)',
  // AI Image Edit — interactive previews. The preview rate also covers
  // small-image edits, where the full-res preview is saved as the final
  // edit (no separate apply charge), so the label calls out both cases.
  auto_enhance_preview: 'Auto Enhance (preview / small-image edit)',
  ai_restore_preview: 'AI Restore (preview / small-image edit)',
  white_balance_preview: 'White Balance (preview / small-image edit)',
  exposure_correction_preview: 'Exposure Correction (preview / small-image edit)',
  color_preview: 'Color Correction (preview / small-image edit)',
  deblur_preview: 'Deblur (preview / small-image edit)',
  face_restore_preview: 'Face Restore (preview / small-image edit)',
  sequence_preview: 'Enhance Sequence (preview / small-image edit)',
  remove_background_preview: 'Remove Background (preview / small-image edit)',
  // Print export rides the ai.photo_edit source: an export beyond the plan's
  // monthly allowance (stage 'overage'), or its optional AI upscale.
  print_export: 'Print Export',
};

export const SOURCE_LABELS: Record<string, string> = {
  'ai.chat': 'AI Chat',
  'ai.bgremove': 'Background Removal',
  'ai.ocr': 'Image Analysis (OCR / Vision)',
  'ai.face': 'Face Recognition',
  'ai.agent': 'Agent Step',
  'ai.photo_edit': 'AI Image Edit',
  // conf/22 — one debit per video run (refunds of failed renditions share it)
  'video.transcode': 'Video Processing',
  admin: 'Sysadmin Adjustment',
  'admin.grant': 'Sysadmin Grant',
  'demo.bootstrap': 'Demo Bootstrap',
  legacy: 'Legacy Pool',
  'migration.legacy_pool': 'Legacy Pool',
  cycle_reset: 'Monthly Grant',
  topup_purchase: 'Top-up Purchase',
  plan_upgrade: 'Plan Upgrade',
};

export const sourceLabel = (entry: AiCreditLedgerEntry): string => {
  const known = SOURCE_LABELS[entry.source];
  if (known) return known;
  return entry.source;
};

// Per-entry label for the Account ledger. For AI Image Edit charges the
// generic "AI Image Edit" source is replaced with the specific operation
// (and stage) read from the entry metadata, so a small-image edit — where
// the preview is saved as the final edit — reads "Auto Enhance (small
// image & preview)" rather than a misleading "Auto Enhance (preview)".
// `GET /stats/ai/ledger` serialises `metadata` as a JSON string (the column is
// JSON, returned as text), so an object can't be assumed — until 2026-10-15
// every AI Image Edit charge fell back to the generic source label.
const entryMetadata = (entry: AiCreditLedgerEntry): Record<string, any> | null => {
  const raw: unknown = entry.metadata;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, any>) : null;
};

export const ledgerEntryLabel = (entry: AiCreditLedgerEntry): string => {
  const meta = entry.source === 'ai.photo_edit' ? entryMetadata(entry) : null;
  if (!meta) {
    return sourceLabel(entry);
  }
  const operation = typeof meta.operation === 'string' ? meta.operation : '';
  if (!operation) return sourceLabel(entry);

  const base = OPERATION_LABELS[operation] ?? operation;
  if (meta.stage === 'overage') return `${base} (beyond monthly allowance)`;
  if (meta.stage === 'upscale') return `${base} (AI upscale)`;
  if (meta.stage !== 'preview') return base;

  const persistedAsFinal =
    meta.persisted_as_final === true || meta.use_lowres === false;
  return persistedAsFinal
    ? `${base} (small image & preview)`
    : `${base} (preview)`;
};
